/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import CaptureStep from '../CaptureStep';
import MultiPhotoReview from './MultiPhotoReview';
import CachedImage from '../CachedImage';
import NotesButton from './NotesButton';
import './capture.css';
import ReferenceTooltip from './ReferenceTooltip';
import { DEFAULT_CUSTOMER_REFERENCE_LABEL, resolveCustomerReferenceLabel } from '../../utils/customerReferenceLabel';
import { toLocal } from '../../utils/dateTime';

// Import logos for light/dark mode
import logoBlack from '../../assets/truvideo-logo-black.png';
import logoWhite from '../../assets/truvideo-logo-white.png';

const Capturing = ({
  assignment,
  workflow,
  tenant,
  currentItem,
  currentStepIndex,
  items,
  capturedFiles,
  serverCompleted,
  completedItemsMedia,
  sessionRecovered,
  branding,
  pageBgStyle,
  welcomeBgClass,
  accentBgClass,
  brandSolidStyle,
  onCapture,
  onSkip,
  onReview,
  onPrevious,
  onRetake,
  onDeletePhoto,
  onAddPhoto,
  token,
  retakeItemId,
  location,
  multiPhotoReviewItemId,
  replaceUploadContext,
  onMultiPhotoAddMore,
  onMultiPhotoRetake,
  onMultiPhotoNext,
  onMultiPhotoBack,
  offlineSupportEnabled = true,
  onOpenNotes,
  onSaveAndExit,
}) => {
  // Caching (not uploads) is skipped entirely when offline support is off — see assetCache.js.
  const cacheToken = offlineSupportEnabled ? token : null;
  const currentTheme = branding.theme || 'light';
  const isDark = ['dark', 'synthwave', 'halloween', 'forest', 'aqua', 'black', 'luxury', 'dracula', 'business', 'night', 'coffee', 'dim', 'sunset'].includes(currentTheme);

  // Use brand logo if available, otherwise use theme-based TruVideo logo
  const brandLogo = workflow?.branding?.logoUrl || '';
  const fallbackLogo = isDark ? logoWhite : logoBlack;

  const [showTooltip, setShowTooltip] = useState(false);
  const [showNameTooltip, setShowNameTooltip] = useState(false);
  const tooltipTriggerRef = useRef(null);
  const nameTooltipTriggerRef = useRef(null);
  const dynamicCustomerReferenceLabel = resolveCustomerReferenceLabel(workflow);

  // Format expiry as readable date/time (same as CaptureHeader)
  const formattedExpiry = (() => {
    if (!assignment?.expiresAt) return null;
    try {
      const expiryDate = new Date(assignment.expiresAt);
      if (isNaN(expiryDate.getTime())) return null;
      if (expiryDate <= new Date()) return 'Expired';
      const options = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true };
      if (expiryDate.getFullYear() !== new Date().getFullYear()) options.year = 'numeric';
      return toLocal(expiryDate, options);
    } catch { return null; }
  })();

  const totalSteps = items.length;

  // Show MultiPhotoReview when a multi-photo step has just been captured
  if (multiPhotoReviewItemId && currentItem?.assignmentItemId === multiPhotoReviewItemId) {
    const entry = capturedFiles[multiPhotoReviewItemId];
    const capturedPhotos = Array.isArray(entry?.photos) ? entry.photos : [];
    const serverPhotos = completedItemsMedia[multiPhotoReviewItemId] ?? [];
    const photos = capturedPhotos.length > 0 ? capturedPhotos : serverPhotos;
    return (
      <MultiPhotoReview
        assignment={assignment}
        workflow={workflow}
        branding={branding}
        token={cacheToken}
        item={currentItem}
        stepNumber={currentStepIndex + 1}
        totalSteps={totalSteps}
        photos={photos}
        onAddMore={onMultiPhotoAddMore}
        onRetakePhoto={(photo, photoIndex) => onMultiPhotoRetake(multiPhotoReviewItemId, photo, photoIndex)}
        onDeletePhoto={(photo) => onDeletePhoto(multiPhotoReviewItemId, photo.mediaFileId, photo.localId)}
        onBack={onMultiPhotoBack}
        onNext={onMultiPhotoNext}
        onOpenNotes={onOpenNotes}
        onSaveAndExit={onSaveAndExit}
      />
    );
  }

  return (
    <div data-theme={branding.theme || 'light'} className="min-h-screen flex flex-col transition-colors duration-500 bg-base-100 text-base-content overflow-x-hidden capture-container">

      {/* Header — Semi-transparent overlay with rounded bottom corners matching reference */}
      <div className="landscape-main-camera absolute top-0 left-3 right-3 bg-black/10 p-4 pb-2 border-b border-black/20 z-20 rounded-b-[1.5rem] shadow-lg overflow-x-hidden">
        <div className="max-w-lg mx-auto space-y-3">

          {/* Row 1: Logo & Expiry */}
          <div className="flex justify-between items-center">
            {brandLogo ? (
              <CachedImage
                token={cacheToken}
                src={brandLogo}
                alt="TruVideo"
                className="w-auto h-[25px] object-contain"
                onFallbackFailed={(e) => { e.target.src = fallbackLogo; }}
              />
            ) : (
              <img src={fallbackLogo} alt="TruVideo" className="w-auto h-[25px] object-contain" />
            )}
            {formattedExpiry && (
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-medium text-base-content/70">
                  Expires:
                </span>
                <div className={`rounded-full py-[6px] px-[10px] font-bold text-[10px] tabular-nums shadow-sm leading-none ${formattedExpiry === 'Expired'
                  ? 'bg-error/10 border border-error/30 text-error'
                  : 'bg-[#FFFBE4] border border-[#A65200] text-[#A65200]'
                  }`}>
                  {formattedExpiry}
                </div>
              </div>
            )}
          </div>

          {/* Row 2: Submitter & Policy/Reference */}
          <div className="flex flex-col gap-1 mt-1">
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0">
                <h2
                  title={assignment.submitterName}
                  ref={nameTooltipTriggerRef}
                  className="text-[18px] font-bold text-base-content leading-none tracking-tight truncate cursor-pointer transition-opacity hover:opacity-80 active:opacity-70"
                  onClick={(e) => { e.stopPropagation(); setShowNameTooltip(!showNameTooltip); }}
                >
                  {assignment.submitterName || assignment.workflowName || 'Mobile Capture'}
                </h2>
                <ReferenceTooltip
                  assignment={assignment}
                  workflow={workflow}
                  show={showNameTooltip}
                  onClose={() => setShowNameTooltip(false)}
                  theme={currentTheme}
                  triggerRef={nameTooltipTriggerRef}
                  type="name"
                />
              </div>
              {onOpenNotes && <NotesButton onClick={onOpenNotes} />}
            </div>
            <div className="shrink-0 overflow-visible text-[12px] text-base-content/70">
              <div className="relative flex justify-between items-start custom-line-break w-full">
                <span>{dynamicCustomerReferenceLabel === DEFAULT_CUSTOMER_REFERENCE_LABEL ? 'Reference' : dynamicCustomerReferenceLabel}:</span>
                <span className="shrink max-w-full inline-block text-right break-all">
                  <button
                    type="button"
                    ref={tooltipTriggerRef}
                    onClick={() => setShowTooltip(!showTooltip)}
                    className="text-primary underline decoration-dashed focus:outline-none custom-line-break text-right"
                  >
                    {assignment.customerReferenceNumber}
                  </button>
                </span>
                <ReferenceTooltip
                  assignment={assignment}
                  workflow={workflow}
                  show={showTooltip}
                  onClose={() => setShowTooltip(false)}
                  theme={currentTheme}
                  triggerRef={tooltipTriggerRef}
                />
              </div>
            </div>
          </div>

          {/* Row 3: Step Progress */}
          <div className="flex items-center gap-3">
            <span className="text-[13px] font-bold text-base-content/60 whitespace-nowrap">
              Step {currentStepIndex + 1} of {totalSteps}
            </span>
            <div className="flex-1 flex gap-1 h-[5px]">
              {items.map((_, i) => (
                <div
                  key={i}
                  className={`flex-1 rounded-full transition-all duration-500 ${i <= currentStepIndex
                    ? 'bg-primary shadow-sm'
                    : 'bg-base-content/10'
                    }`}
                />
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 px-4 pt-40 pb-32 overflow-y-auto relative z-10 w-full max-w-xl mx-auto">
        <div className="animate-fade-in" style={{ animationDelay: '200ms' }}>
          {currentItem && (
            <CaptureStep
              key={currentItem.assignmentItemId}
              assignment={assignment}
              tenant={tenant}
              item={currentItem}
              stepNumber={currentStepIndex + 1}
              totalSteps={totalSteps}
              onCapture={onCapture}
              onRetake={onRetake}
              onDeletePhoto={onDeletePhoto}
              onAddPhoto={onAddPhoto}
              onBack={onPrevious}
              onNext={onSkip}
              uploading={false}
              uploadProgress={0}
              branding={branding}
              capturedData={capturedFiles[currentItem.assignmentItemId]}
              isServerCompleted={serverCompleted[currentItem.assignmentItemId]}
              completedMedia={completedItemsMedia[currentItem.assignmentItemId]}
              token={token}
              retakeMode={retakeItemId === currentItem.assignmentItemId}
              workflow={workflow}
              location={location}
              replaceUploadContext={replaceUploadContext}
              offlineSupportEnabled={offlineSupportEnabled}
              onOpenNotes={onOpenNotes}
              onSaveAndExit={onSaveAndExit}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Capturing;

Capturing.propTypes = {
  assignment: PropTypes.shape({
    expiresAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    submitterName: PropTypes.string,
    workflowName: PropTypes.string,
    customerReferenceNumber: PropTypes.string,
  }),
  workflow: PropTypes.shape({
    branding: PropTypes.shape({
      logoUrl: PropTypes.string,
    }),
  }),
  tenant: PropTypes.object,
  currentItem: PropTypes.shape({
    assignmentItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    required: PropTypes.bool,
  }),
  currentStepIndex: PropTypes.number,
  items: PropTypes.arrayOf(PropTypes.object),
  capturedFiles: PropTypes.object,
  serverCompleted: PropTypes.object,
  completedItemsMedia: PropTypes.object,
  sessionRecovered: PropTypes.bool,
  branding: PropTypes.shape({
    theme: PropTypes.string,
  }),
  pageBgStyle: PropTypes.any,
  welcomeBgClass: PropTypes.string,
  accentBgClass: PropTypes.string,
  brandSolidStyle: PropTypes.any,
  onCapture: PropTypes.func,
  onSkip: PropTypes.func,
  onReview: PropTypes.func,
  onPrevious: PropTypes.func,
  onRetake: PropTypes.func,
  onDeletePhoto: PropTypes.func,
  onAddPhoto: PropTypes.func,
  token: PropTypes.string,
  retakeItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  location: PropTypes.object,
  multiPhotoReviewItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onMultiPhotoAddMore: PropTypes.func,
  onMultiPhotoRetake: PropTypes.func,
  onMultiPhotoNext: PropTypes.func,
  onMultiPhotoBack: PropTypes.func,
  offlineSupportEnabled: PropTypes.bool,
  onOpenNotes: PropTypes.func,
  onSaveAndExit: PropTypes.func,
};